/**
 * User Activity Tracker Service
 * Tracks user activities including page visits, time spent, form submissions, etc.
 */

import backendApi from './backendApi';

class ActivityTracker {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.pageStartTime = null;
    this.currentPage = null;
    this.activityQueue = [];
    this.isTracking = false;
    this.flushInterval = null;
    this.heartbeatInterval = null;
    
    // Initialize tracking
    this.init();
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize activity tracking
   */
  init() {
    if (this.isTracking) return;
    
    this.isTracking = true;
    
    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseTracking();
      } else {
        this.resumeTracking();
      }
    });

    // Track before page unload
    window.addEventListener('beforeunload', () => {
      this.trackPageLeave();
      this.flushQueue();
    });

    // Flush queue periodically (every 30 seconds)
    this.flushInterval = setInterval(() => {
      this.flushQueue();
    }, 30000);

    // Heartbeat is disabled to prevent duplicate entries
    // Time tracking is done when leaving the page
    // this.heartbeatInterval = setInterval(() => {
    //   this.sendHeartbeat();
    // }, 60000);
  }

  /**
   * Track page visit
   * Only logs when leaving a page, not on entry to avoid duplicates
   */
  trackPageVisit(pageUrl, pageTitle, metadata = {}) {
    const previousPage = this.currentPage;
    const timeSpent = this.pageStartTime 
      ? Math.floor((Date.now() - this.pageStartTime) / 1000) 
      : 0;

    // Only log previous page if user spent meaningful time (> 2 seconds)
    // This prevents duplicate entries for quick navigation
    if (previousPage && timeSpent >= 2) {
      // Only log if it's a different page (avoid duplicate for same page)
      if (previousPage.url !== pageUrl) {
        this.logActivity({
          activity_type: 'page_visit',
          page_url: previousPage.url,
          page_title: previousPage.title,
          time_spent_seconds: timeSpent,
          metadata: {
            ...previousPage.metadata,
            left_at: new Date().toISOString()
          }
        });
      }
    }

    // Start tracking new page (don't log entry yet)
    this.currentPage = {
      url: pageUrl,
      title: pageTitle,
      metadata: metadata,
      startTime: Date.now()
    };
    this.pageStartTime = Date.now();
    // Note: We don't log the page visit on entry to avoid duplicates
    // It will be logged when leaving the page or on heartbeat
  }

  /**
   * Track form submission
   */
  trackFormSubmit(formId, formData, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'form_submit',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Form submitted: ${formId}`,
      form_data: formData,
      metadata: {
        ...metadata,
        form_id: formId,
        submitted_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track data view
   */
  trackDataView(dataType, dataId, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'data_view',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Viewed ${dataType}: ${dataId}`,
      metadata: {
        ...metadata,
        data_type: dataType,
        data_id: dataId,
        viewed_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track data edit
   */
  trackDataEdit(dataType, dataId, changes, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'data_edit',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Edited ${dataType}: ${dataId}`,
      form_data: changes,
      metadata: {
        ...metadata,
        data_type: dataType,
        data_id: dataId,
        edited_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track data delete
   */
  trackDataDelete(dataType, dataId, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'data_delete',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Deleted ${dataType}: ${dataId}`,
      metadata: {
        ...metadata,
        data_type: dataType,
        data_id: dataId,
        deleted_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track navigation
   */
  trackNavigation(fromUrl, toUrl, navigationType = 'link', metadata = {}) {
    this.logActivity({
      activity_type: 'navigation',
      page_url: fromUrl,
      page_title: `Navigation: ${fromUrl} → ${toUrl}`,
      action_description: `Navigated from ${fromUrl} to ${toUrl}`,
      metadata: {
        ...metadata,
        from_url: fromUrl,
        to_url: toUrl,
        navigation_type: navigationType,
        navigated_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track download
   */
  trackDownload(fileName, fileType, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'download',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Downloaded: ${fileName}`,
      metadata: {
        ...metadata,
        file_name: fileName,
        file_type: fileType,
        downloaded_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track export
   */
  trackExport(exportType, format, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'export',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Exported ${exportType} as ${format}`,
      metadata: {
        ...metadata,
        export_type: exportType,
        format: format,
        exported_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track search
   */
  trackSearch(searchQuery, resultsCount, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'search',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Searched: ${searchQuery}`,
      metadata: {
        ...metadata,
        search_query: searchQuery,
        results_count: resultsCount,
        searched_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track filter
   */
  trackFilter(filterType, filterValue, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: 'filter',
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: `Applied filter: ${filterType} = ${filterValue}`,
      metadata: {
        ...metadata,
        filter_type: filterType,
        filter_value: filterValue,
        filtered_at: new Date().toISOString()
      }
    });
  }

  /**
   * Track custom activity
   */
  trackCustomActivity(activityType, description, pageUrl, pageTitle, metadata = {}) {
    this.logActivity({
      activity_type: activityType,
      page_url: pageUrl || this.currentPage?.url,
      page_title: pageTitle || this.currentPage?.title,
      action_description: description,
      metadata: {
        ...metadata,
        activity_at: new Date().toISOString()
      }
    });
  }

  /**
   * Check if current user is a superuser (skip tracking)
   */
  isSuperuser() {
    // Check if user info is available in localStorage or context
    try {
      const authData = localStorage.getItem('authData');
      if (authData) {
        const parsed = JSON.parse(authData);
        const user = parsed?.user || parsed;
        return user?.is_superuser || user?.isSuperuser || false;
      }
    } catch (e) {
      // Ignore errors
    }
    return false;
  }

  /**
   * Log activity (adds to queue)
   */
  logActivity(activityData) {
    // Skip tracking for superusers
    if (this.isSuperuser()) {
      return;
    }

    const activity = {
      ...activityData,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    };

    this.activityQueue.push(activity);

    // Flush queue if it gets too large
    if (this.activityQueue.length >= 10) {
      this.flushQueue();
    }
  }

  /**
   * Track page leave
   * Only logs if user spent meaningful time (> 1 second)
   */
  trackPageLeave() {
    if (this.currentPage && this.pageStartTime) {
      const timeSpent = Math.floor((Date.now() - this.pageStartTime) / 1000);
      // Only log if user spent at least 1 second on the page
      if (timeSpent >= 1) {
        this.logActivity({
          activity_type: 'page_visit',
          page_url: this.currentPage.url,
          page_title: this.currentPage.title,
          time_spent_seconds: timeSpent,
          metadata: {
            ...this.currentPage.metadata,
            left_at: new Date().toISOString()
          }
        });
      }
      // Reset tracking
      this.currentPage = null;
      this.pageStartTime = null;
    }
  }

  /**
   * Send heartbeat to track active time
   * Only updates time spent, doesn't create new entries
   * This prevents duplicate log entries
   */
  sendHeartbeat() {
    // Heartbeat is now only used for internal tracking
    // We don't log it to avoid duplicate entries
    // The final time will be logged when leaving the page
    if (this.currentPage && this.pageStartTime && !document.hidden) {
      // Just update internal tracking, don't log
      // The time will be captured when user leaves the page
    }
  }

  /**
   * Pause tracking
   * Don't log on pause, just track internally
   */
  pauseTracking() {
    // Don't log on pause to avoid duplicate entries
    // Time will be captured when page is left or resumed
  }

  /**
   * Resume tracking
   */
  resumeTracking() {
    if (this.currentPage) {
      this.pageStartTime = Date.now();
    }
  }

  /**
   * Flush activity queue to backend
   */
  async flushQueue() {
    if (this.activityQueue.length === 0) return;

    const activities = [...this.activityQueue];
    this.activityQueue = [];

    try {
      // Send activities one by one (or batch if backend supports it)
      for (const activity of activities) {
        await backendApi.logUserActivity(activity);
      }
    } catch (error) {
      console.error('Error flushing activity queue:', error);
      // Re-add failed activities to queue (limit to prevent infinite growth)
      if (this.activityQueue.length < 100) {
        this.activityQueue.unshift(...activities);
      }
    }
  }

  /**
   * Stop tracking
   */
  stop() {
    this.isTracking = false;
    this.trackPageLeave();
    this.flushQueue();
    
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
      this.flushInterval = null;
    }
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
}

// Create singleton instance
const activityTracker = new ActivityTracker();

export default activityTracker;

